import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { inviteInfo, loginPassword, registerInvited, resendVerification, verifyEmail, type InviteInfo } from '../lib/api';
import { LOGO_URL } from '../lib/config';
import { useApp } from '../app/AppContext';

export function Invite() {
  const { reload } = useApp();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [logoOk, setLogoOk] = useState(true);
  const [info, setInfo] = useState<InviteInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState<'set' | 'otp'>('set');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [pwVisible, setPwVisible] = useState(false);
  const [code, setCode] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    inviteInfo(token).then(setInfo).catch(() => setInfo({ valid: false })).finally(() => setLoading(false));
  }, [token]);

  async function submitSet(e: React.FormEvent) {
    e.preventDefault(); setErr(null);
    if (password.length < 8) { setErr('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setErr('Passwords don’t match.'); return; }
    setBusy(true);
    try { await registerInvited(info!.email!, password, displayName.trim() || undefined); setStep('otp'); }
    catch (e2) { setErr(e2 instanceof Error ? e2.message : 'Could not register.'); }
    finally { setBusy(false); }
  }

  async function submitOtp(e: React.FormEvent) {
    e.preventDefault(); setErr(null); setBusy(true);
    try {
      await verifyEmail(info!.email!, code.trim());
      await loginPassword(info!.email!, password);
      const denied = await reload(); // signed in → provisioned as the invited role; app re-renders to the shop
      if (denied) { sessionStorage.removeItem('scb.loginError'); setErr(denied); setBusy(false); }
    } catch (e2) { setErr(e2 instanceof Error ? e2.message : 'Could not verify the code.'); setBusy(false); }
  }

  const Logo = () => logoOk
    ? <img className="logo" src={LOGO_URL} alt="Stray Cat Blues" onError={() => setLogoOk(false)} />
    : <div className="fallback" style={{ fontSize: '1.4rem' }}>🐾 Stray Cat Blues</div>;

  if (loading) return <div className="login-wrap"><div className="login-card"><Logo /><p className="lead">Checking your invitation…</p></div></div>;

  if (!info?.valid) {
    return (
      <div className="login-wrap"><div className="login-card">
        <Logo />
        <h1>Invitation not valid</h1>
        <p className="lead">This invitation link is invalid or has expired. Please ask a Stray Cat Blues admin to send a new one.</p>
        <a className="btn secondary" href="/">Go to sign in</a>
      </div></div>
    );
  }

  return (
    <div className="login-wrap">
      <div className="login-card">
        <Logo />
        <h1>Welcome to Stray Cat Blues</h1>
        <p className="lead">You&rsquo;ve been invited as a <b>{info.role}</b>. Finish setting up your account for <b>{info.email}</b>.</p>
        {err && <div className="notice err">{err}</div>}

        {step === 'set' ? (
          <form className="stack" onSubmit={submitSet} style={{ textAlign: 'left' }}>
            <label className="field">Your name (optional)
              <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} autoComplete="name" />
            </label>
            <label className="field">Create a password
              <span className="pw-wrap">
                <input type={pwVisible ? 'text' : 'password'} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
                <button type="button" className="pw-toggle" onClick={() => setPwVisible((v) => !v)} aria-label={pwVisible ? 'Hide password' : 'Show password'}>
                  {pwVisible
                    ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                    : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>}
                </button>
              </span>
            </label>
            <label className="field">Confirm password
              <input type={pwVisible ? 'text' : 'password'} autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
            </label>
            <button className="btn" type="submit" disabled={busy}>{busy ? 'Creating…' : 'Create account'}</button>
          </form>
        ) : (
          <form className="stack" onSubmit={submitOtp} style={{ textAlign: 'left' }}>
            <div className="notice info" style={{ margin: 0 }}>We emailed a 6-digit code to <b>{info.email}</b>. Enter it below to finish.</div>
            <label className="field">Verification code
              <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" placeholder="123456" required />
            </label>
            <button className="btn" type="submit" disabled={busy || code.length !== 6}>{busy ? 'Verifying…' : 'Verify & sign in'}</button>
            <button type="button" className="btn-link" onClick={() => { void resendVerification(info!.email!); setErr(null); }}>Resend code</button>
          </form>
        )}
      </div>
    </div>
  );
}
